import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef, useCallback, useMemo } from 'react'
import { APIProvider, Map, AdvancedMarker, InfoWindow, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'

// images
import markerImg from '../../../../assets/img/leeflet/marker-icon.png'
import redMarkerImg from '@/assets/img/schedule/icons/pin-red.svg'
import popupDefaultHeadImage from "@/assets/img/modals/icons/head.svg"
import reservationMarker from '@/assets/img/schedule/icons/pin.svg'

import '../../../../assets/css/modals/employees.css'
import { Maps } from '../../../../_config/maps'
import loader from '../../../../assets/img/loader.svg'
import Api from '../../../../_helpers/Api'
import Dropdown from '../../../partials/Dropdown'
import { trans } from '../../../../_providers/Translation'
import DateRange from '@/components/partials/DateRange'
import { FormControlLabel, Switch } from '@mui/material'

let timeout

// Route cache to avoid redundant Directions API calls
let cc = {}

const CENTER = { lat: 42.70060440808085, lng: 23.318481445312504 }

// ─── Outer wrapper: provides the Google Maps API context ─────────────────────
function Address(props, ref) {
    return (
        <APIProvider apiKey={Maps.google}>
            <AddressContent {...props} forwardedRef={ref} />
        </APIProvider>
    )
}

// ─── Main content component ───────────────────────────────────────────────────
function AddressContent({ forwardedRef, ...props }) {
    const inputRef = useRef(null)

    const placesLib = useMapsLibrary('places')

    const [state, setState] = useState({
        overlay: false,
        modal: false,
        loading: false,
        loadingNearest: false,
        selected: null,
        lat: null,
        lon: null,
        place_id: null,
        data: [],
        dataLoaded: false,
        reservations: [],
        nearest: [],
        employees: [],
        nearestEmployees: [],
        onSave: null,
        showNearest: true,
        selectedEmployee: null,
    })

    const [services, setServices] = useState({
        autocomplete: null,
        geocoder: null,
    })

    useImperativeHandle(forwardedRef, () => ({
        open: () => handleOpen(),
        close: () => handleClose(),
        onSave: fn => setState(prev => ({ ...prev, onSave: fn })),
        cancelLoading: () => setState(prev => ({ ...prev, loading: false, }))
    }))

    // Initialize Google Places / Geocoder services once the library is ready
    useEffect(() => {
        if (placesLib && window.google) {
            setServices({
                autocomplete: new placesLib.AutocompleteService(),
                geocoder: new window.google.maps.Geocoder(),
            })
        }
    }, [placesLib])

    // Sync props
    useEffect(() => {
        if (Array.isArray(props.reservations)) {
            setState(prev => ({ ...prev, reservations: props.reservations }))
        }
    }, [props.reservations])

    useEffect(() => {
        if (Array.isArray(props.employees)) {
            setState(prev => ({ ...prev, employees: props.employees }))
        }
    }, [props.employees])

    useEffect(() => {
        handleSelect(props.selected)
    }, [props.selected])

    // Debounced address search
    useEffect(() => {
        findLocations()
        return () => clearTimeout(timeout)
    }, [state.selected, state.showNearest])

    // Fetch nearest employee reservations when coords change
    useEffect(() => {
        if (state.showNearest && state.lat && state.lon) {
            setState(prev => ({ ...prev, loadingNearest: true }))

            const url = 'reservations/nearest-employees'
                + '?lat=' + state.lat
                + '&lon=' + state.lon
                + '&date=' + props.filter.start
                + '&end=' + props.filter.end
                + '&category_id=' + props.filter.category_id

            Api.get(url)
                .then(res => {
                    if (res.data.reservations) {
                        setState(prev => ({
                            ...prev,
                            nearest: res.data.reservations,
                            nearestEmployees: res.data.employees,
                            selectedEmployee: res.data.reservations[0]
                                ? res.data.reservations[0].employee_id
                                : null,
                        }))
                    }
                })
                .finally(() => {
                    setState(prev => ({ ...prev, loadingNearest: false }))
                })
        }
    }, [state.showNearest, state.lat, state.lon, props.filter.start, props.filter.end])

    // Reset location data when "showNearest" is toggled off
    useEffect(() => {
        if (!state.showNearest) {
            setState(prev => ({
                ...prev,
                nearest: [],
                nearestEmployees: [],
                data: [],
                lat: null,
                lon: null,
            }))
        }
    }, [state.showNearest])

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleOpen = () => {
        setState(prev => ({ ...prev, overlay: true }))
        setTimeout(() => setState(prev => ({ ...prev, modal: true })), 50)
    }

    const handleClose = e => {
        if (e) e.preventDefault()
        setState(prev => ({ ...prev, modal: false }))
        setTimeout(() => setState(prev => ({ ...prev, overlay: false })), 50)
    }

    const handleSelect = name => {
        setState(prev => ({ ...prev, selected: name }))
    }

    const handleSelectLatLon = useCallback((lat, lon, place_id = null) => {
        setState(prev => ({ ...prev, lat, lon, place_id }))
    }, [])

    const handleSelectEmployee = id => {
        setState(prev => ({
            ...prev,
            selectedEmployee: prev.selectedEmployee === id ? null : id
        }))
    }

    const handleSearch = e => {
        const name = e.target.value
        setState(prev => ({ ...prev, selected: name }))
        if (typeof props.handleSearch === 'function') {
            props.handleSearch('search', name)
        }
    }

    const findLocations = () => {
        if (!state.selected || !state.showNearest) return
        setState(prev => ({ ...prev, loading: true }))
        clearTimeout(timeout)
        timeout = setTimeout(() => findInGoogle(state.selected), 900)
    }

    const findInGoogle = name => {
        if (!services.autocomplete) return

        services.autocomplete.getQueryPredictions({ input: name }, (predictions, status) => {
            if (!predictions || status !== window.google.maps.places.PlacesServiceStatus.OK) {
                setState(prev => ({ ...prev, data: [], loading: false }))
                return
            }

            const mapped = predictions.map(p => ({ ...p, display_name: p.description }))
            setState(prev => ({ ...prev, data: mapped, loading: false }))
        })
    }

    const handleSelectGooglePlace = place => {
        if (!services.geocoder) return

        services.geocoder.geocode({ placeId: place.place_id }).then(({ results }) => {
            if (results && results[0]) {
                const lat = results[0].geometry.location.lat()
                const lon = results[0].geometry.location.lng()
                handleSelectLatLon(lat, lon, place.place_id)
            }
        })
    }

    const handleAddReservation = (employeeId, date, periodId) => {
        if (employeeId && date && periodId) {
            date = date.split(' ')[0]
            const url = '/schedule/reservations/address/add'
                + '?employee_id=' + employeeId
                + '&date=' + date
                + '&visit_period_id=' + periodId
                + '&address=' + state.selected
                + '&lat=' + state.lat
                + '&lon=' + state.lon
                + '&redirect=schedule'
            window.location = url
        }
    }

    const toggleShowNearest = bool => {
        setState(prev => ({ ...prev, showNearest: bool }))
    }

    const handleCategory = data => {
        const val = data.id || ''
        if (data.id || data.name === '') {
            props.handleSearch('category_id', val)
        }
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className={`${state.overlay ? 'visible' : ''} overlay`}>
            <div className={`${state.modal ? 'show' : ''} popup-primary`}>

                <div className='head-new'>
                    <img src={popupDefaultHeadImage} alt="icon" />
                    <div className='texts'>
                        <h2>{trans('headings.schedule')}</h2>
                        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
                    </div>
                    <span className='close' onClick={handleClose}></span>
                </div>

                <div className="filter-standard">
                    <div className="col">
                        <DateRange
                            startDate={props.filter.start}
                            endDate={props.filter.end}
                            onStartDateChange={date => props.handleSearch('start', date)}
                            onEndDateChange={date => props.handleSearch('end', date)}
                        />
                    </div>
                    <div className="col">
                        <Dropdown
                            inputIdValue={Number(props.filter.category_id)}
                            url="appliances/categories/all"
                            renderText={data => data.name}
                            onChange={data => handleCategory(data)}
                            inputPlaceholder={trans('filters.category')}
                        />
                    </div>
                    <div className="col" style={{ justifyContent: 'center' }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={state.showNearest}
                                    onChange={e => toggleShowNearest(e.target.checked)}
                                />
                            }
                            label={trans('messages.showEmployees')}
                        />
                    </div>
                </div>

                <hr />

                <div className="alert info">
                    {state.showNearest
                        ? trans('messages.showNearest1')
                        : trans('messages.showNearest2')
                    }
                </div>

                <div id="map-wrapper" style={{ height: '45vh' }}>
                    <div className="left">
                        <input
                            ref={inputRef}
                            type="search"
                            name="search"
                            value={state.selected || ''}
                            onChange={handleSearch}
                            style={{ width: '100%' }}
                        />
                        {state.loading
                            ? <img className="loader" src={loader} />
                            : state.data.length === 0
                                ? <div className="alert info">
                                   Търсете, за да видите резултати.
                                </div>
                                :
                                <ul>
                                    {state.data.map(e =>
                                        <li
                                            key={'e-' + e.place_id}
                                            onClick={() => handleSelectGooglePlace(e)}
                                            className={e.place_id === state.place_id ? 'active' : ''}
                                        >
                                            <span>{e.display_name}</span>
                                        </li>
                                    )}
                                </ul>
                        }
                    </div>

                    <div className="right">
                        <div
                            className="map"
                            style={state.loadingNearest
                                ? { filter: 'blur(3px)', pointerEvents: 'none' }
                                : {}
                            }
                        >
                            <Map
                                defaultCenter={CENTER}
                                defaultZoom={5}
                                mapId="DEMO_MAP_ID"
                                style={{ width: '100%', height: '100%' }}
                                disableDefaultUI={true}
                                keyboardShortcuts={false}
                            >
                                <MapOverlays
                                    state={state}
                                    handleSelectLatLon={handleSelectLatLon}
                                    handleAddReservation={handleAddReservation}
                                />
                            </Map>
                        </div>

                        <div className="left options">
                            <ul>
                                {state[state.showNearest ? 'nearestEmployees' : 'employees'].map(e =>
                                    <li
                                        key={'e-' + e.id}
                                        className={Number(e.id) === Number(state.selectedEmployee) ? 'active' : ''}
                                        onClick={() => handleSelectEmployee(e.id)}
                                        style={{ display: 'flex', alignItems: 'center' }}
                                    >
                                        <span>{e.name} {e.lastname}</span>
                                        <div className="badge">{e.reservations_count || '0'}</div>
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className="footer align-right">
                    <button onClick={handleClose}>
                        {trans('buttons.close')}
                    </button>
                </div>

            </div>
        </div>
    )
}

// ─── Map overlays (must live inside <Map> context) ────────────────────────────
function MapOverlays({ state, handleSelectLatLon, handleAddReservation }) {
    const map = useMap()
    const routesLib = useMapsLibrary('routes')

    const [openInfoWindow, setOpenInfoWindow] = useState(null)
    const directionsRenderersRef = useRef([])

    // Close any open InfoWindow when the selected employee is cleared
    useEffect(() => {
        if (!state.selectedEmployee) {
            setOpenInfoWindow(null)
        }
    }, [state.selectedEmployee])

    // Compute the set of reservation markers to display.
    // lat/lon are cast to float here so Google Maps never receives string values.
    const visibleMarkers = useMemo(() => {
        const source = state.showNearest ? state.nearest : state.reservations
        const filtered = !state.selectedEmployee
            ? source
            : source.filter(r => Number(r.employee_id) === Number(state.selectedEmployee))
        return filtered.map(r => ({ ...r, lat: parseFloat(r.lat), lon: parseFloat(r.lon) }))
    }, [state.showNearest, state.nearest, state.reservations, state.selectedEmployee])

    // Auto-open the first marker popup when in showNearest mode
    useEffect(() => {
        if (state.showNearest && visibleMarkers.length > 0) {
            setOpenInfoWindow(`m-${visibleMarkers[0].id ?? 0}`)
        }
    }, [visibleMarkers, state.showNearest])

    // Re-register map click listener whenever showNearest changes
    useEffect(() => {
        if (!map) return
        const listener = map.addListener('click', e => {
            if (state.showNearest) {
                handleSelectLatLon(e.latLng.lat(), e.latLng.lng())
            }
        })
        return () => window.google.maps.event.removeListener(listener)
    }, [map, state.showNearest, handleSelectLatLon])

    // Trigger a resize so the map fills its container when the modal opens
    useEffect(() => {
        if (map && state.overlay && state.modal) {
            window.google.maps.event.trigger(map, 'resize')
        }
    }, [map, state.overlay, state.modal])

    // Fit the viewport to all visible markers
    useEffect(() => {
        if (!map) return

        if (!state.showNearest) {
            if (visibleMarkers.length === 0) return
            const bounds = new window.google.maps.LatLngBounds()
            visibleMarkers.forEach(r => bounds.extend({ lat: r.lat, lng: r.lon }))
            map.fitBounds(bounds)
            return
        }

        // showNearest mode: fit selected location + employee markers
        if (!state.lat || !state.lon) return
        const bounds = new window.google.maps.LatLngBounds()
        bounds.extend({ lat: state.lat, lng: state.lon })
        visibleMarkers.forEach(r => bounds.extend({ lat: r.lat, lng: r.lon }))
        if (!bounds.isEmpty()) map.fitBounds(bounds)

    }, [map, state.lat, state.lon, state.showNearest, visibleMarkers])

    // Draw driving routes from selected location to each visible employee marker
    useEffect(() => {
        if (!map || !routesLib) return

        // Clear previous route renderers
        directionsRenderersRef.current.forEach(dr => dr.setMap(null))
        directionsRenderersRef.current = []

        if (!state.showNearest || !state.lat || !state.lon || visibleMarkers.length === 0) return

        const ds = new routesLib.DirectionsService()

        visibleMarkers.forEach(r => {
            const cacheKey = `${state.lat}${state.lon}${r.lat}${r.lon}`
            const dr = new routesLib.DirectionsRenderer({
                map,
                suppressMarkers: true,
                preserveViewport: true,
            })

            const applyRoute = result => {
                dr.setDirections(result)
                directionsRenderersRef.current.push(dr)
            }

            if (cc[cacheKey]) {
                applyRoute(cc[cacheKey])
                return
            }

            ds.route(
                {
                    origin: { lat: state.lat, lng: state.lon },
                    destination: { lat: r.lat, lng: r.lon },
                    travelMode: routesLib.TravelMode.DRIVING,
                },
                (result, status) => {
                    if (status === routesLib.DirectionsStatus.OK) {
                        cc[cacheKey] = result
                        applyRoute(result)
                    }
                }
            )
        })

        return () => {
            directionsRenderersRef.current.forEach(dr => dr.setMap(null))
        }
    }, [map, routesLib, state.lat, state.lon, state.showNearest, visibleMarkers])

    return (
        <>
            {/* Red marker — selected reservation location */}
            {state.lat && state.lon && (
                <>
                    <AdvancedMarker
                        position={{ lat: state.lat, lng: state.lon }}
                        onClick={() => setOpenInfoWindow('reservation')}
                    >
                        <img src={redMarkerImg} width={20} height={23} alt="selected location" />
                    </AdvancedMarker>

                    {openInfoWindow === 'reservation' && (
                        <InfoWindow
                            position={{ lat: state.lat, lng: state.lon }}
                            onCloseClick={() => setOpenInfoWindow(null)}
                            headerContent={trans('random.choosedLocation')}
                        />
                    )}
                </>
            )}

            {/* Blue markers — employee reservation locations */}
            {visibleMarkers.map((r, idx) => {
                const key = `m-${r.id ?? idx}`
                return (
                    <React.Fragment key={key}>
                        <AdvancedMarker
                            position={{ lat: r.lat, lng: r.lon }}
                            onClick={() => setOpenInfoWindow(key)}
                        >
                            <img src={reservationMarker} width={20} height={23} alt="reservation" />
                        </AdvancedMarker>

                        {openInfoWindow === key && (
                            <InfoWindow
                                position={{ lat: r.lat, lng: r.lon }}
                                onCloseClick={() => setOpenInfoWindow(null)}
                                headerContent={<b style={{ fontFamily: "Inter Bold" }}>{`#${r.reference_code}`}</b>}
                            >
                                <div style={{ fontSize: 13, lineHeight: 1.6 }}>
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    {trans('random.technician')}:
                                                </td>
                                                <td>
                                                    {r.employee ? `${r.employee.name} ${r.employee.lastname}` : '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.date')}:
                                                </td>
                                                <td>
                                                    {r.visit_date_dmy}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.hour')}:
                                                </td>
                                                <td>
                                                    {r.visitperiod ? r.visitperiod.period : '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.address')}:
                                                </td>
                                                <td>
                                                    {r.full_address}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.client')}:
                                                </td>
                                                <td>
                                                    {r.customer ? `${r.customer.name} ${r.customer.lastname}` : '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.phone')}:
                                                </td>
                                                <td>
                                                    {r.phone || '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.category')}:
                                                </td>
                                                <td>
                                                    {r.appliancecategory?.name || '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.make')}:
                                                </td>
                                                <td>
                                                    {r.appliancebrand?.name || '-'}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {trans('random.model')}:
                                                </td>
                                                <td>
                                                    {r.appliancemodel?.name || '-'}
                                                </td>
                                            </tr>
                                            {state.showNearest ?
                                                <tr>
                                                    <td>
                                                        {trans('random.distance')}:
                                                    </td>
                                                    <td>
                                                        {r.distance?.toFixed(2)} км.
                                                    </td>
                                                </tr>
                                                :
                                                <>
                                                </>
                                            }
                                        </tbody>
                                    </table>
                                    {state.showNearest && (
                                        <>
                                            <button
                                                className="add button-add button-extra-small"
                                                style={{ marginTop: 5 }}
                                                onClick={() => {
                                                    handleAddReservation(r.employee_id, r.visit_date, r.visit_period_id)
                                                    setOpenInfoWindow(null)
                                                }}
                                            >
                                                {trans('buttons.add')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </InfoWindow>
                        )}
                    </React.Fragment>
                )
            })}
        </>
    )
}

export default React.memo(forwardRef(Address), (prev, next) => {
    return JSON.stringify(prev) === JSON.stringify(next)
})
